// One-time migration of a user's legacy (VBLive API) data into Firestore.
//
// Runs the first time a user logs in after this feature ships and has not yet
// been migrated.  The migration is idempotent: every document uses a
// deterministic id (the legacy session id / a slug of the team / tournament
// name) so re-running it simply overwrites the same documents.  A status
// document at `migrations/{uid}` records completion so it only runs once.
//
// Data model written to Firestore (see the design notes in the chat history):
//   matches/{sessionId}      -> light metadata used by the Matches List / queries
//   matchData/{sessionId}    -> the heavy compressed event buffer (fetched on demand)
//   teams/{uid}_{slug}       -> team + embedded players roster
//   tournaments/{uid}_{slug} -> tournament name
//   migrations/{uid}         -> { status, version, migratedAt, counts }

import {
  doc,
  getDoc,
  serverTimestamp,
  setDoc,
  writeBatch,
} from "firebase/firestore";
import { db } from "../../firebase.config";
import {
  fetchAllMyMatches,
  fetchSessionById,
} from "./dbutils";
import { generateMatch } from "./DVWFile";
import { initWithPSVBCompressedBuffer } from "./PSVBFile";
import { collectTeamsAndPlayers } from "./teamsPlayers";
import { unzipBuffer } from "./Utils";
import { myunzip } from "./zip";

// Bump this when the migration logic changes in a way that requires re-running.
// v2: stores match `origin` and the parsed teams/players model.
export const MIGRATION_VERSION = 2;

// ---- Cached "is migrated" flag -------------------------------------------
// The data layer (firestoreData.js) needs a synchronous answer to "should I
// read from Firestore?" on every fetch.  We cache the per-user answer in
// localStorage; the MigrationGate refreshes it on login (from the authoritative
// Firestore status doc) and after a successful migration.
function migratedFlagKey(uid) {
  return `vblive-migrated-${uid}`;
}

export function isMigratedCached(uid) {
  if (!uid) return false;
  try {
    return localStorage.getItem(migratedFlagKey(uid)) === "1";
  } catch {
    return false;
  }
}

export function setMigratedCached(uid, value) {
  if (!uid) return;
  try {
    if (value) localStorage.setItem(migratedFlagKey(uid), "1");
    else localStorage.removeItem(migratedFlagKey(uid));
  } catch {
    /* ignore storage errors */
  }
}

// Firestore document ids may not contain "/" and must be <= 1500 bytes.  Slug
// team / tournament names into a safe, stable id fragment.
function slugify(value) {
  return String(value ?? "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 120);
}

// Session ids from the legacy API are numeric / GUID strings; guard against any
// stray "/" just in case.
function safeId(value) {
  return String(value ?? "").replace(/\//g, "_");
}

// Strip undefined values (Firestore rejects them) and normalise the legacy
// match metadata into the light `matches` document shape.
function matchMetaDoc(m, firebaseUser, origin) {
  return {
    legacyId: safeId(m.id),
    teamA: m.teamA ?? "",
    teamB: m.teamB ?? "",
    description: m.description ?? "",
    tournament: m.tournament ?? "",
    scores: m.scores ?? "",
    origin: origin ?? null,
    sessionDateTimeInSeconds: m.sessionDateTimeInSeconds ?? null,
    sessionDateString: m.sessionDateString ?? "",
    videoOnlineUrl: m.videoOnlineUrl ?? null,
    videoStartTimeSeconds: m.videoStartTimeSeconds ?? null,
    videoOffset: m.videoOffset ?? null,
    shareStatus: m.shareStatus ?? null,
    shareUsers: m.shareUsers ?? null,
    appName: m.appName ?? "VBLive",
    owner: firebaseUser.uid,
    ownerEmail: firebaseUser.email ?? null,
    migratedAt: serverTimestamp(),
  };
}

// Decompress a legacy stats buffer (LZW/base64 via myzip, or gzip).
function decompressStats(stats) {
  let buf = null;
  try {
    buf = myunzip(stats);
  } catch {
    buf = null;
  }
  if (!buf || buf.length === 0) {
    try {
      buf = unzipBuffer(stats);
    } catch {
      buf = null;
    }
  }
  return buf;
}

// Detect the match origin and parse the teams/players from a legacy session.
// Returns { origin, parsed } where parsed has teamA/teamB { Name, players }.
function parseSession(session) {
  const stats = session?.stats;
  if (!stats) return { origin: null, parsed: null };
  const buf = decompressStats(stats);
  const isPSVB = buf ? buf.includes("PSVB") : false;
  const origin = isPSVB ? "PSVB" : "DVW";
  let parsed = null;
  try {
    parsed = isPSVB
      ? initWithPSVBCompressedBuffer(stats)
      : generateMatch(buf);
  } catch (e) {
    console.log("Failed to parse session for teams/players", e);
    parsed = null;
  }
  return { origin, parsed };
}

/**
 * Returns the migration status document for a user, or null when the user has
 * never been migrated.
 */
export async function getMigrationStatus(uid) {
  if (!uid) return null;
  try {
    const snap = await getDoc(doc(db, "migrations", uid));
    return snap.exists() ? snap.data() : null;
  } catch (e) {
    console.log("Failed to read migration status", e);
    return null;
  }
}

/**
 * True when the user still needs to be migrated to Firestore.
 */
export async function needsMigration(uid) {
  const status = await getMigrationStatus(uid);
  const migrated =
    !!status &&
    status.status === "done" &&
    (status.version ?? 0) >= MIGRATION_VERSION;
  // Keep the synchronous cache in sync with the authoritative status doc so the
  // data layer reads from the right source on every device.
  setMigratedCached(uid, migrated);
  return !migrated;
}

/**
 * Migrate a user's legacy data into Firestore.
 *
 * @param {object}   firebaseUser  the authenticated Firebase user (uid + email)
 * @param {function} onProgress    called with { phase, done, total, label }
 * @returns {object} { ok, counts, errors }
 */
export async function runMigration({ firebaseUser, onProgress } = {}) {
  if (!firebaseUser?.uid || !firebaseUser?.email) {
    return { ok: false, counts: {}, errors: ["Not signed in."] };
  }
  const uid = firebaseUser.uid;
  const email = firebaseUser.email;
  const errors = [];
  const counts = { matches: 0, teams: 0, players: 0, tournaments: 0 };
  const report = (phase, done, total, label) =>
    onProgress?.({ phase, done, total, label });

  // Mark the migration as in-progress so other devices/tabs don't double-run.
  try {
    await setDoc(
      doc(db, "migrations", uid),
      {
        status: "in-progress",
        version: MIGRATION_VERSION,
        startedAt: serverTimestamp(),
        ownerEmail: email,
      },
      { merge: true }
    );
  } catch (e) {
    console.log("Failed to mark migration in-progress", e);
  }

  // ---- 1. Matches (metadata + compressed buffer) ----
  report("matches", 0, 1, "Loading your matches…");
  let matches = [];
  try {
    matches = (await fetchAllMyMatches(email)) ?? [];
  } catch (e) {
    console.log("Failed to fetch matches", e);
    errors.push("Could not load your matches.");
  }

  const uniqueTournaments = new Map(); // slug -> name
  const teamsMap = new Map(); // teamDocId -> team document (parsed roster)
  const playersMap = new Map(); // playerDocId -> player document

  for (let i = 0; i < matches.length; i++) {
    const m = matches[i];
    const idText = `${m.teamA ?? "?"} vs ${m.teamB ?? "?"}`;
    report("matches", i, matches.length, idText);

    // Collect tournaments to migrate afterwards (deduplicated).
    const tslug = slugify(m.tournament);
    if (tslug && !uniqueTournaments.has(tslug)) {
      uniqueTournaments.set(tslug, m.tournament);
    }

    try {
      const id = safeId(m.id);

      // Load + decompress the buffer to detect origin and parse teams/players.
      const session = await fetchSessionById(m.id);
      const { origin, parsed } = parseSession(session);

      // Light metadata document (drives the list / queries).
      await setDoc(doc(db, "matches", id), matchMetaDoc(m, firebaseUser, origin));

      // Heavy compressed buffer document (fetched only when a match is opened).
      if (session?.stats) {
        await setDoc(doc(db, "matchData", id), {
          buffer: session.stats, // legacy LZW/base64 (or gzip) buffer, stored as-is
          codec: "legacy",
          owner: uid,
          ownerEmail: email,
          migratedAt: serverTimestamp(),
        });
      }

      // Accumulate parsed teams + players (deduped across all matches).
      if (parsed) {
        collectTeamsAndPlayers(
          parsed,
          {
            uid,
            email,
            origin,
            sessionId: id,
            seconds: m.sessionDateTimeInSeconds ?? null,
          },
          teamsMap,
          playersMap
        );
      }
      counts.matches += 1;
    } catch (e) {
      console.log("Failed to migrate match", m.id, e);
      errors.push(`Match "${idText}" could not be migrated.`);
    }
    report("matches", i + 1, matches.length, idText);
  }

  // ---- 2. Teams + players (parsed from the match buffers above) ----
  // Count both so the bar advances smoothly through the whole phase (80→95%).
  const teamDocs = Array.from(teamsMap.values());
  const playerDocs = Array.from(playersMap.values());
  const totalTeamsPlayers = teamDocs.length + playerDocs.length;

  report("teams", 0, totalTeamsPlayers, "Migrating teams…");
  for (let i = 0; i < teamDocs.length; i++) {
    const team = teamDocs[i];
    report("teams", i, totalTeamsPlayers, team.name);
    try {
      await setDoc(doc(db, "teams", team.id), {
        name: team.name,
        nameLower: team.nameLower,
        rosterHash: team.rosterHash,
        playerCount: team.playerCount,
        players: team.players,
        playerIds: team.playerIds,
        origin: team.origin ?? null,
        lastSessionId: team.lastSessionId ?? null,
        lastSeenSeconds: team.lastSeenSeconds ?? null,
        owner: uid,
        ownerEmail: email,
        migratedAt: serverTimestamp(),
      });
      counts.teams += 1;
    } catch (e) {
      console.log("Failed to migrate team", team.name, e);
      errors.push(`Team "${team.name}" could not be migrated.`);
    }
    report("teams", i + 1, totalTeamsPlayers, team.name);
  }

  // ---- 2b. Players ----
  for (let i = 0; i < playerDocs.length; i++) {
    const p = playerDocs[i];
    const label = `${p.lastName ?? ""} ${p.firstName ?? ""}`.trim();
    report("teams", teamDocs.length + i, totalTeamsPlayers, label);
    try {
      await setDoc(doc(db, "players", p.id), {
        firstName: p.firstName,
        lastName: p.lastName,
        nickName: p.nickName,
        nameLower: p.nameLower,
        positions: p.positions ?? [],
        teamIds: p.teamIds,
        teamNames: p.teamNames,
        owner: uid,
        ownerEmail: email,
        migratedAt: serverTimestamp(),
      });
      counts.players = (counts.players ?? 0) + 1;
    } catch (e) {
      console.log("Failed to migrate player", p.firstName, p.lastName, e);
      errors.push(`Player "${p.firstName} ${p.lastName}" could not be migrated.`);
    }
    report("teams", teamDocs.length + i + 1, totalTeamsPlayers, label);
  }

  // ---- 3. Tournaments (small docs, batched) ----
  const tournamentEntries = Array.from(uniqueTournaments.entries());
  report("tournaments", 0, tournamentEntries.length, "Migrating tournaments…");
  try {
    let batch = writeBatch(db);
    let ops = 0;
    for (let i = 0; i < tournamentEntries.length; i++) {
      const [slug, name] = tournamentEntries[i];
      batch.set(doc(db, "tournaments", `${uid}_${slug}`), {
        name: name ?? "",
        owner: uid,
        ownerEmail: email,
        migratedAt: serverTimestamp(),
      });
      ops += 1;
      counts.tournaments += 1;
      // Commit in chunks well under Firestore's 500-op batch limit.
      if (ops >= 400) {
        await batch.commit();
        batch = writeBatch(db);
        ops = 0;
      }
      report("tournaments", i + 1, tournamentEntries.length, name);
    }
    if (ops > 0) await batch.commit();
  } catch (e) {
    console.log("Failed to migrate tournaments", e);
    errors.push("Some tournaments could not be migrated.");
  }

  // ---- 4. Finalise ----
  report("finalizing", 1, 1, "Finishing up…");
  const ok = errors.length === 0;
  try {
    await setDoc(doc(db, "migrations", uid), {
      status: ok ? "done" : "completed-with-errors",
      version: MIGRATION_VERSION,
      migratedAt: serverTimestamp(),
      ownerEmail: email,
      counts,
      errorCount: errors.length,
    });
  } catch (e) {
    console.log("Failed to write migration status", e);
  }

  // Only flip the synchronous cache on a clean migration so a partial run keeps
  // reading from the legacy API until it is retried and completes.
  setMigratedCached(uid, ok);

  return { ok, counts, errors };
}
